/** @jsx isolateComponent */

import { useState } from 'react';

import { Modal } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Colors, SkeletonBlock } from '@zapier/design-system';

import { ChangePlan } from '@zapier/design-system-revenue/src/components/other';

import { useTaskLimitUpgradeNudges } from '@zapier/design-system-revenue/src/hooks';

import {
  PlanType,
  TrialExtensionReason,
} from '@zapier/design-system-revenue/src/types';

import { useExtendTrial, useNextPlan } from './hooks';
import { Celebrate } from './Celebrate';
import { UpgradeReason } from './UpgradeReason';

const MODAL_STATE = {
  reasons: 'reasons',
  upgrade: 'upgrade',
  celebrateTrial: 'celebrateTrial',
  celebrateUpgrade: 'celebrateUpgrade',
  error: 'error',
};

type Props = {
  appName?: string;
  onBack?: () => void;
  onClose: () => void;
  onUpgradeFinish?: () => void;
  /**
   * Deprecated: plan to upgrade to is automatically determined now.
   */
  upgradeToPlan?: any;
  validationReason: TrialExtensionReason;
};

export const UpgradeModal = ({
  appName,
  onBack,
  onClose,
  onUpgradeFinish = () => {},
  validationReason,
}: Props) => {
  const [modalState, setModalState] = useState(MODAL_STATE.reasons);

  const {
    extendTrial,
    error: extendTrialError,
    isLoading: isExtendingTrial,
  } = useExtendTrial({
    reason: validationReason,
    shouldAutoUpgrade: true,
  });

  const {
    isLoading: isLoadingTaskLimitUpgradeNudges,
    isEligibleFor80PercentTaskUpgradeNudge,
    isEligibleFor100PercentTaskUpgradeNudge,
  } = useTaskLimitUpgradeNudges();

  const { isLoading: isLoadingNextPlan, nextPlan } = useNextPlan({
    interval: 'year',
  });

  if (
    !nextPlan ||
    (validationReason === 'tasks' &&
      !isEligibleFor100PercentTaskUpgradeNudge) ||
    (validationReason === 'tasks-80-percent' &&
      !isEligibleFor80PercentTaskUpgradeNudge)
  ) {
    return null;
  }

  const celebratePlan = {
    limits: {
      action_limit: nextPlan?.tasks,
      premium_apps_limit: nextPlan?.premiumAppsLimit,
      zap_limit: nextPlan?.zapLimit,
    },
    name: nextPlan?.name,
    type: nextPlan?.type as PlanType,
  };

  const isLoading =
    isExtendingTrial || isLoadingTaskLimitUpgradeNudges || isLoadingNextPlan;

  const onExtendTrial = async () => {
    try {
      await extendTrial();
      setModalState(MODAL_STATE.celebrateTrial);
    } catch {
      setModalState(MODAL_STATE.error);
    }
  };

  const onUpgradeClick = () => {
    setModalState(MODAL_STATE.upgrade);
  };

  const onUpgradeComplete = () => {
    onUpgradeFinish();
    setModalState(MODAL_STATE.celebrateUpgrade);
  };

  const stateToModalContents = {
    [MODAL_STATE.reasons]: (
      <UpgradeReason
        appName={appName}
        onBack={onBack}
        onClose={onClose}
        onExtendTrial={onExtendTrial}
        onUpgradeClick={onUpgradeClick}
        plan={{ ...nextPlan, type: nextPlan.type as PlanType }}
        reason={validationReason}
      />
    ),
    [MODAL_STATE.upgrade]: (
      <ChangePlan
        changeType={'upgrade'}
        isCompact={true}
        onCancel={onClose}
        onFinishChangePlan={onUpgradeComplete}
        showSuccessModal={false}
        toPlanName={nextPlan.name.toLowerCase()}
      />
    ),
    [MODAL_STATE.celebrateTrial]: (
      <Celebrate onClose={onClose} plan={celebratePlan} reason="trial" />
    ),
    [MODAL_STATE.celebrateUpgrade]: (
      <Celebrate onClose={onClose} plan={celebratePlan} reason="upgrade" />
    ),
    [MODAL_STATE.error]: (
      <Celebrate
        errorMessage={extendTrialError}
        onClose={onClose}
        plan={celebratePlan}
        reason="error"
      />
    ),
  };

  const modalContents = stateToModalContents[modalState];

  return (
    <Modal
      backgroundColor={Colors.grey000}
      closeOnOutsideClick={true}
      maxWidth={895}
      onClose={onClose}
    >
      {isLoading ? (
        <SkeletonBlock height={335} width="100%" />
      ) : (
        <div
          css={{
            backgroundColor: Colors.grey000,
          }}
        >
          {modalContents}
        </div>
      )}
    </Modal>
  );
};
